import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import EmailNav from "./EmailNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Search, Send, Calendar as CalIcon, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatET } from "@/lib/dates";

const STATUS_BADGE = {
  draft: "bg-muted text-foreground",
  scheduled: "bg-amber-500/15 text-amber-500",
  sending: "bg-blue-500/15 text-blue-500",
  sent: "bg-emerald-500/15 text-emerald-500",
};

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/email/campaigns")
      .then(({ data }) => setCampaigns(data.campaigns || []))
      .catch(() => toast.error("Failed to load campaigns"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this campaign?")) return;
    try { await api.delete(`/email/campaigns/${id}`); toast.success("Deleted"); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Delete failed"); }
  };

  const filtered = campaigns.filter((c) => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (q && !`${c.name} ${c.subject}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="campaigns-page">
      <EmailNav />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Campaigns</h2>
          <p className="text-xs text-muted-foreground">{campaigns.length} total</p>
        </div>
        <Button size="sm" onClick={() => navigate("/email/campaigns/new")} data-testid="campaigns-new-btn">
          <Plus size={14} className="mr-2" /> New Campaign
        </Button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="campaigns-search" placeholder="Search campaigns..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="campaigns-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="sending">Sending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-sm text-muted-foreground">
          {campaigns.length === 0 ? (
            <>No campaigns yet. <button onClick={() => navigate("/email/campaigns/new")} className="text-primary hover:underline">Create your first campaign →</button></>
          ) : "No campaigns match those filters."}
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <div className="divide-y divide-border">
            {filtered.map((c) => (
              <button
                key={c.id}
                data-testid={`campaign-row-${c.id}`}
                onClick={() => navigate(`/email/campaigns/${c.id}`)}
                className="w-full text-left p-4 flex items-center gap-3 flex-wrap hover:bg-accent/30 transition-colors"
              >
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail size={14} className="text-muted-foreground" />
                    <h3 className="font-semibold text-sm">{c.name}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{c.subject}</p>
                </div>
                <Badge className={STATUS_BADGE[c.status] || "bg-muted"}>
                  {c.status === "scheduled" && <CalIcon size={10} className="mr-1" />}
                  {c.status === "sent" && <Send size={10} className="mr-1" />}
                  {c.status}
                </Badge>
                {c.status === "scheduled" && c.scheduled_at && (
                  <span className="text-xs text-muted-foreground">{formatET(c.scheduled_at)}</span>
                )}
                {c.status === "sent" && (
                  <span className="text-xs text-muted-foreground">
                    {c.stats?.sent || 0} sent · {c.stats?.unique_opens || 0} opens · {c.stats?.unique_clicks || 0} clicks
                  </span>
                )}
                {c.status === "draft" && c.created_at && (
                  <span className="text-xs text-muted-foreground hidden md:inline">{formatET(c.created_at)}</span>
                )}
                <Button variant="ghost" size="icon" data-testid={`campaign-delete-${c.id}`} onClick={(e) => remove(e, c.id)}>
                  <Trash2 size={14} className="text-rose-500" />
                </Button>
              </button>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
